// Pure parser for the inline `__PAY_CARD__{json}` marker emitted by the
// build-cost tracker when Mark asks to pay a supplier. The card shows the
// payee, IBAN, amount and reference as tap-to-copy chips and opens the bank
// app, so a payment is paste-and-approve instead of typing an IBAN by hand.

import { inlineJsonMarker } from './inlineJsonMarker'

export const PAY_CARD_MARKER = '__PAY_CARD__'

export type PayCardBank = 'revolut' | 'aib'

export type PayCardPayload = {
  payee: string
  account_holder?: string
  iban: string
  bic?: string
  amount: string
  currency?: string
  reference: string
  invoice_number?: string
  due_date?: string
  banks?: PayCardBank[]
  note?: string
}

const payCardMarker = inlineJsonMarker<PayCardPayload>(PAY_CARD_MARKER)

export const parsePayCard = payCardMarker.parse
export const splitPayCard = payCardMarker.split

/** Android package ids, confirmed against Google Play listings (2026-10-04). */
export const BANK_APPS: Record<PayCardBank, { label: string; packageName: string; webUrl: string }> = {
  revolut: { label: 'Revolut', packageName: 'com.revolut.revolut', webUrl: 'https://app.revolut.com/' },
  // The bare onlinebanking.aib.ie host answers 403 Forbidden; the login page is the working entry (25 Oct 2026).
  aib: { label: 'AIB', packageName: 'aib.ibank.android', webUrl: 'https://onlinebanking.aib.ie/inet/roi/login.htm' },
}

/** Chrome-on-Android intent that launches the bank app's main activity, falling
 * back to the bank's web banking if the app is not installed. */
export function bankAppIntentUrl(bank: PayCardBank): string {
  const app = BANK_APPS[bank]
  return `intent://#Intent;action=android.intent.action.MAIN;category=android.intent.category.LAUNCHER;`
    + `package=${app.packageName};S.browser_fallback_url=${encodeURIComponent(app.webUrl)};end`
}

export function groupIban(iban: string): string {
  return iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
}

export function formatPayAmount(amount: string, currency = 'EUR'): string {
  const n = Number(amount)
  if (!Number.isFinite(n)) return `${currency} ${amount}`
  const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : `${currency} `
  return `${symbol}${n.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
